import React from "react";
import { animated, useSpring } from "react-spring";
import { Box, makeStyles, Paper } from "@material-ui/core";

import { useIntl } from "@eroad/i18n/useIntl";
import { ArrowBack as ArrowBackIcon } from "@eroad/icons/material";
import { Tooltip } from "@eroad/ui-library/Tooltip";

import useWindowSize from "../hooks/useWindowSize";
import type { CoretexTheme } from "../theme";

const AnimatedPaper = animated(Paper);
const TabIcon = animated(ArrowBackIcon);

const useStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    position: "relative",
  },
  tab: {
    height: 20,
    width: 20,
    position: "absolute",
    top: "16px",
    left: "0px",
    cursor: "pointer",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.elevation.standard,
  },
  arrow: {
    height: 16,
    width: 16,
    fill: theme.palette.mono.secondaryDark,
  },
  panel: {
    transform: "rotate(0deg)",
    WebkitTransform: "rotate(0deg)",
    MsTransform: "rotate(0deg)",
    MozTransform: "rotate(0deg)",
    zIndex: 1,
    backgroundColor: "white",
    height: "100%",
    overflow: "hidden",
  },
}));

const animationPaperConfig = {
  mass: 2,
  friction: 20,
  tension: 120,
  clamp: true,
};

export interface InspectorPanelProps {
  children?: React.ReactNode;
  panelOpen?: boolean;
  onToggle?: (state: string) => void;
  isMobile?: boolean;
}

const defaultWidth = 380;
const defaultPaddingPx = 8;
const arrowOffsetPx = 10;

export const InspectorPanel = ({ children, panelOpen, onToggle, isMobile }: InspectorPanelProps): JSX.Element => {
  const { t } = useIntl();

  const classes = useStyles();
  const [panelWidth, setPanelWidth] = React.useState<number>(!isMobile ? defaultWidth : window.innerWidth);
  const [innerWidth] = useWindowSize(200);
  const [open, setOpen] = React.useState<boolean>(panelOpen ?? true);
  React.useEffect(() => {
    if (panelOpen !== undefined) setOpen(panelOpen);
  }, [panelOpen]);
  React.useEffect(() => {
    if (onToggle) onToggle(open.toString());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useLayoutEffect(() => {
    if (isMobile) {
      setPanelWidth(innerWidth);
    } else {
      setPanelWidth(defaultWidth);
    }
  }, [innerWidth, isMobile]);

  const openAnimation = useSpring({
    marginLeft: open ? 0 : -(panelWidth - defaultPaddingPx),
    width: defaultWidth,
    config: animationPaperConfig,
  });
  const openMobileAnimation = useSpring({
    transform: open ? "translateX(0px)" : `translateX(-${panelWidth}px)`,
    config: animationPaperConfig,
  });
  const openTabAnimation = useSpring({
    transform: open ? `translateX(${panelWidth - arrowOffsetPx}px)` : "translateX(0px)",
    config: animationPaperConfig,
  });
  const rotateAnimation = useSpring({ transform: open ? "rotate(0deg)" : "rotate(180deg)" });

  return (
    <Box className={classes.root}>
      {!isMobile && (
        <Tooltip title={open ? t("inspectorPanel.collapse") : t("inspectorPanel.expand")}>
          <AnimatedPaper
            className={classes.tab}
            square
            style={openTabAnimation}
            id="ipanel-tab"
            onClick={() => setOpen(!open)}
            elevation={3}
          >
            <TabIcon className={classes.arrow} style={rotateAnimation} />
          </AnimatedPaper>
        </Tooltip>
      )}

      <AnimatedPaper
        square
        className={classes.panel}
        data-testid="iPanel"
        id="iPanel"
        style={isMobile ? { ...openMobileAnimation, position: "fixed" } : openAnimation}
        elevation={3}
      >
        <Box display="flex" flexDirection="column" height="100%" width={panelWidth}>
          {children}
        </Box>
      </AnimatedPaper>
    </Box>
  );
};
